'use client'
import Image from 'next/image'

import { CHANNELTIVITY_URLS } from '@/constants/channeltivity'
import { useLocale } from '@/context/LocaleContext'
import { useTranslation } from '@/hooks/useTranslation'
import { trackEvent } from '@/utils/analytics'

export const PARTNER_PORTAL_CTA_CLICKED = 'partner_portal_cta_clicked'

type PartnerPortalCtaProps = {
  labelKey: 'become_a_partner' | 'deal_registration_form'
  /** Target of the "Already a partner?" link. */
  portalLink?: PortalLink
  /** Page uid, sent as `source` on the click event. */
  source?: string
  className?: string
}

type PortalLink = 'login' | 'deal_reg'

const PORTAL_LINK_HREFS: Record<PortalLink, string> = {
  login: CHANNELTIVITY_URLS.LOGIN,
  deal_reg: CHANNELTIVITY_URLS.DEAL_REG,
}

// No EVENT_CONFIG entry, so clicks stay PostHog-only: a click is not an application and must not count
// as an ad conversion.
export default function PartnerPortalCta({ labelKey, portalLink = 'login', source, className }: PartnerPortalCtaProps) {
  const { t } = useTranslation(useLocale())

  const handleClick = (destination: 'become_a_partner' | PortalLink) => () => {
    trackEvent(PARTNER_PORTAL_CTA_CLICKED, { destination, ...(source ? { source } : {}) })
  }

  return (
    <div className={className}>
      <a
        className="btn btn-primary btn-default btn-interaction !w-fit !gap-2 !font-halyardText !text-base !no-underline"
        href={CHANNELTIVITY_URLS.BECOME_A_PARTNER}
        target="_blank"
        rel="noopener noreferrer"
        onClick={handleClick('become_a_partner')}>
        {t(labelKey)} <span className="sr-only">({t('opens_in_new_tab')})</span>
        <Image src="/images/angle-right-green.svg" width={20} height={16} alt="" aria-hidden="true" />
      </a>
      <p className="mt-6 text-sm text-primary-ink-85">
        {t('already_a_partner')}{' '}
        <a
          className="!font-halyardText !text-sm text-primary-green-150"
          href={PORTAL_LINK_HREFS[portalLink]}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`${t('access_partner_portal')} (${t('opens_in_new_tab')})`}
          onClick={handleClick(portalLink)}>
          {t('access_partner_portal')}
        </a>
        .
      </p>
    </div>
  )
}
